import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '../../store/slices/notificationSlice';
import DummyUser from '../../assets/images/avatar.png';
import { Spinner, Button, Card, Badge } from 'react-bootstrap';

export const NotificationsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { notifications, unreadCount, pagination, loading, error } = useSelector(
    (state) => state.notifications || {
      notifications: [],
      unreadCount: 0,
      pagination: { total: 0, page: 0, limit: 15, pages: 0, hasNextPage: false },
      loading: false,
      error: null,
    }
  );

  const [page, setPage] = useState(0);
  const limit = 15;

  useEffect(() => {
    dispatch(fetchNotifications({ page, limit }));
  }, [dispatch, page]);

  const handleNotificationClick = (item) => {
    const id = item._id || item.id;
    const isUnread = !(item.read || item.isRead);
    if (isUnread && id) {
      dispatch(markNotificationAsRead(id));
    }
    const actionUrl = item.action?.actionUrl || item.link || item.url;
    const actionModel = item.action?.actionModel || item.model;
    const actionId = item.action?.actionId || item.actionId;

    const nav = getNavigationTarget(actionUrl, actionModel, actionId);
    if (nav?.path) {
      if (nav.id) {
        navigate(nav.path, { state: { id: nav.id } });
      } else {
        navigate(nav.path);
      }
    }
  };

  const getNavigationTarget = (actionUrl, actionModel, actionId) => {
    const model = (actionModel || '').toLowerCase();
    
    if (model.includes('class')) {
      return { path: '/class-detail', id: actionId };
    }
    if (model.includes('course')) {
      return { path: '/course-detail', id: actionId };
    }
    if (model.includes('event')) {
      return { path: '/event-detail', id: actionId };
    }
    if (model.includes('lesson')) {
      return { path: '/lesson-detail', id: actionId };
    }
    if (model.includes('teacher')) {
      return { path: '/teachers-details', id: actionId };
    }
    if (model.includes('community')) {
      return { path: '/community-detail', id: actionId };
    }
    if (model.includes('post')) {
      return { path: '/video-feed' };
    }

    if (actionUrl) {
      const cleanUrl = actionUrl.trim();
      if (cleanUrl === '/posts' || cleanUrl.startsWith('/post')) {
        return { path: '/video-feed' };
      }
      if (cleanUrl === '/class' || cleanUrl === '/classes' || cleanUrl.startsWith('/class-detail')) {
        return { path: '/class-detail', id: actionId };
      }
      if (cleanUrl === '/course' || cleanUrl === '/courses' || cleanUrl.startsWith('/course-detail')) {
        return { path: '/course-detail', id: actionId };
      }
      if (cleanUrl === '/community' || cleanUrl === '/communities' || cleanUrl.startsWith('/community-detail')) {
        return { path: '/community-detail', id: actionId };
      }
      if (cleanUrl === '/event' || cleanUrl === '/events' || cleanUrl.startsWith('/event-detail')) {
        return { path: '/event-detail', id: actionId };
      }
      if (cleanUrl === '/lesson' || cleanUrl === '/lessons' || cleanUrl.startsWith('/lesson-detail')) {
        return { path: '/lesson-detail', id: actionId };
      }
      if (cleanUrl === '/teacher' || cleanUrl === '/teachers' || cleanUrl.startsWith('/teachers')) {
        return { path: '/teachers-details', id: actionId };
      }
      return { path: cleanUrl, id: actionId };
    }
    
    return null;
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now - then;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return diffMins <= 1 ? 'just now' : `${diffMins} minutes ago`;
      }
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    }
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return `about ${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllNotificationsAsRead());
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasNextPage || page < (pagination.pages - 1)) {
      setPage(page + 1);
    }
  };

  return (
    <div className="notifications-page-wrapper py-5 bg-light min-vh-100">
      <div className="container-xl">
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center gap-2">
                <h2 className="fw-bold m-0 text-gray-900">Notifications</h2>
                {unreadCount > 0 && (
                  <Badge bg="danger" className="rounded-pill px-2 py-1" style={{ fontSize: '0.85rem' }}>
                    {unreadCount} new
                  </Badge>
                )}
              </div>
              {unreadCount > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="rounded-pill px-3 fw-semibold"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as read
                </Button>
              )}
            </div>

            {error && (
              <div className="alert alert-danger shadow-sm rounded-3 mb-4" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
              </div>
            )}

            <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
              {loading && notifications.length === 0 ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" className="mb-2" />
                  <p className="text-muted m-0">Loading your notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-5 px-3">
                  <div className="bg-light d-inline-flex p-3 rounded-circle mb-3">
                    <i className="bi bi-bell-slash text-muted fs-2"></i>
                  </div>
                  <h5 className="fw-bold text-gray-800">No notifications yet</h5>
                  <p className="text-muted text-sm mx-auto mb-0" style={{ maxWidth: '320px' }}>
                    We'll keep you posted when there are updates, bookings, or new posts.
                  </p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {notifications.map((item) => {
                    const isUnread = !(item.read || item.isRead);
                    const nTitle = item.notification?.title || item.title || '';
                    const nMessage = item.notification?.message || item.message || item.content || '';
                    const nTime = item.receivedAt || item.createdAt || item.notification?.sentAt || item.timestamp;
                    const avatar = item.notification?.profile_picture || item.profile_picture || DummyUser;

                    return (
                      <div
                        key={item._id || item.id}
                        className={`list-group-item list-group-item-action d-flex align-items-start gap-3 p-4 border-bottom pointer ${isUnread ? 'bg-light-purple-10' : ''}`}
                        onClick={() => handleNotificationClick(item)}
                        style={{ borderBottom: '1px solid #f3f4f6' }}
                      >
                        <div className="notification-avatar border rounded-circle overflow-hidden flex-shrink-0" style={{ width: '42px', height: '42px' }}>
                          <img src={avatar} className="w-100 h-100 object-fit-cover" alt="avatar" />
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            {nTitle && (
                              <h6 className={`m-0 text-sm ${isUnread ? 'fw-bold text-dark' : 'fw-semibold text-gray-800'}`}>
                                {nTitle}
                              </h6>
                            )}
                            <span className="text-xs text-muted">
                              {getTimeAgo(nTime)}
                            </span>
                          </div>
                          <p className={`m-0 text-sm mt-1 ${isUnread ? 'fw-medium text-dark' : 'text-gray-600'}`}>
                            {nMessage}
                          </p>
                        </div>
                        {isUnread && (
                          <div className="d-flex align-self-center ms-2">
                            <span className="bg-primary rounded-circle" style={{ width: '8px', height: '8px' }}></span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Pagination Controls */}
            {pagination.pages > 1 && (
              <div className="d-flex align-items-center justify-content-between mt-4">
                <Button
                  variant="white"
                  className="border shadow-sm rounded-pill px-4 py-2 text-sm fw-semibold"
                  onClick={handlePrevPage}
                  disabled={page === 0 || loading}
                >
                  <i className="bi bi-chevron-left me-1"></i> Previous
                </Button>
                
                <span className="text-sm fw-semibold text-gray-700">
                  Page {page + 1} of {pagination.pages}
                </span>

                <Button
                  variant="white"
                  className="border shadow-sm rounded-pill px-4 py-2 text-sm fw-semibold"
                  onClick={handleNextPage}
                  disabled={(!pagination.hasNextPage && page >= (pagination.pages - 1)) || loading}
                >
                  Next <i className="bi bi-chevron-right ms-1"></i>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
